import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Badge, Banner, Button, Card, Spinner } from "@magarac/ui";
import { DATA_EXPORT_KEEP_DAYS } from "@magarac/shared/config";
import {
  dataExportListSchema,
  dataExportViewSchema,
  type DataExportView,
} from "@magarac/shared/schemas/exports";
import { HelpIcon } from "../../../components/HelpIcon";
import { api } from "../../../lib/api";
import { messageFor } from "../../../lib/errors";
import { useTenant } from "../../../lib/tenant";

export const Route = createFileRoute("/_app/settings/data")({
  component: DataPage,
});

function sizeText(bytes: number | null): string {
  if (bytes === null) return "";
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function whenText(iso: string): string {
  return new Date(iso).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

const STATUS: Record<
  DataExportView["status"],
  { label: string; tone: "info" | "ok" | "danger" | "neutral" }
> = {
  running: { label: "Getting it ready", tone: "info" },
  ready: { label: "Ready", tone: "ok" },
  failed: { label: "Did not finish", tone: "danger" },
  expired: { label: "Deleted", tone: "neutral" },
};

/** BIL-10: download a copy of everything (owner). Screen key: settings.data */
function DataPage() {
  const { active } = useTenant();
  const isOwner = active?.role === "owner";
  const queryClient = useQueryClient();
  const key = ["data-exports", active?.id];
  const q = useQuery({
    queryKey: key,
    queryFn: () => api.get("/tenant/exports", dataExportListSchema),
    enabled: Boolean(active) && isOwner,
    // Check every few seconds while a copy is being made; download links last 15 minutes.
    refetchInterval: (query) =>
      query.state.data?.exports.some((e) => e.status === "running") ? 3000 : 10 * 60_000,
  });
  const start = useMutation({
    mutationFn: () => api.post("/tenant/exports", {}, dataExportViewSchema),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: key }),
  });
  const exports = q.data?.exports ?? [];
  const running = exports.some((e) => e.status === "running");

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Your data</h1>
        <HelpIcon screen="settings.data" />
      </div>
      <Card title="Download all your data" className="mb-4">
        <p className="mb-3">
          Get a copy of everything in {active?.name ?? "your business"}: customers, jobs, visits,
          quotes, invoices, payments, messages, and settings, each as a spreadsheet, plus all your
          job photos, in one zip file.
        </p>
        {!isOwner ? (
          <Banner tone="info">Only the owner can download all of the business&apos;s data.</Banner>
        ) : (
          <>
            {start.error ? <Banner tone="danger">{messageFor(start.error)}</Banner> : null}
            <Button onClick={() => start.mutate()} busy={start.isPending} disabled={running}>
              {running ? "Getting your copy ready…" : "Make a copy of my data"}
            </Button>
            <p className="mt-2 text-sm text-muted">
              It usually takes a minute or two. We will email you when it is ready. Each copy is
              kept for {DATA_EXPORT_KEEP_DAYS} days.
            </p>
          </>
        )}
      </Card>
      {isOwner ? (
        <Card title="Your copies">
          {q.isPending ? (
            <Spinner />
          ) : q.error ? (
            <Banner tone="danger">{messageFor(q.error)}</Banner>
          ) : exports.length === 0 ? (
            <p className="text-muted">No copies yet.</p>
          ) : (
            <ul className="divide-y divide-line">
              {exports.map((e) => (
                <li key={e.id} className="flex flex-wrap items-center justify-between gap-2 py-3">
                  <span>
                    <span className="block font-medium">Made {whenText(e.createdAt)}</span>
                    <span className="block text-sm text-muted">
                      {e.status === "ready" && e.deleteAt
                        ? `${sizeText(e.sizeBytes)}, ${e.fileCount ?? 0} files. Kept until ${whenText(e.deleteAt)}.`
                        : e.status === "failed"
                          ? "Something went wrong. Make a new copy; if it happens again, contact us."
                          : null}
                    </span>
                  </span>
                  <span className="flex items-center gap-3">
                    <Badge tone={STATUS[e.status].tone}>{STATUS[e.status].label}</Badge>
                    {e.downloadUrl ? (
                      <a
                        href={e.downloadUrl}
                        className="rounded-md bg-brand-600 px-3 py-2 text-sm font-semibold text-white hover:bg-brand-700"
                      >
                        Download
                      </a>
                    ) : null}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      ) : null}
    </div>
  );
}
